import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import { findLineForCode, suggestedQuantity, recordScan, isLineComplete } from '../utils/loadingScan';
import ScanFeedback from './ScanFeedback';
import { clearSavedLoad } from '../utils/cargaBetaStorage';
import '../styles/completarpedido.css';

function Completarpedido() {
    const { ponumber } = useParams();
    const { token } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [userId, setUserId] = useState(null);
    const [reportError, setReportError] = useState(false);
    const [errorType, setErrorType] = useState('');
    const [errorExplanation, setErrorExplanation] = useState('');
    const [showBetaMode, setShowBetaMode] = useState(false);
    const [poData, setPoData] = useState(null);
    const [scannedItems, setScannedItems] = useState([]);
    const [currentBarcode, setCurrentBarcode] = useState('');
    const [currentQuantity, setCurrentQuantity] = useState('');
    // The entry just registered, shown as a receipt until the next scan. It
    // keeps the list from before the entry so "Deshacer" is a plain restore.
    const [receipt, setReceipt] = useState(null);

    useEffect(() => {
        const fetchPoData = async () => {
            // First try to use order data from navigation state
            if (location.state?.orderData) {
                setUserId(location.state.orderData.checker_responsable?.[0]?.user_id || null);
                setPoData(location.state.orderData);
                return;
            }

            // If no order data in state, fetch from get-po endpoint
            if (!token) {
                console.error('No token available');
                return;
            }

            try {
                const response = await fetch(`${API_BASE}/get-po/?po_number=${ponumber}`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Token ${token}`,
                    },
                });

                if (response.ok) {
                    const data = await response.json();
                    setUserId(data.checker_responsable?.[0]?.user_id || null);
                    setPoData(data);
                } else {
                    console.error("Error fetching PO data:", response.statusText);
                }
            } catch (error) {
                console.error("Fetch error:", error);
            }
        };

        fetchPoData();
    }, [ponumber, token, location.state]);

    const handleCompleteOrder = async () => {
        if (reportError) {
            const errorRequestBody = {
                user_id: userId,
                po_number: ponumber,
                error_type: errorType,
                error_description: errorExplanation,
            };

            try {
                const errorResponse = await fetch(`${API_BASE}/error-po/`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Token ${token}`,
                    },
                    body: JSON.stringify(errorRequestBody),
                });

                if (!errorResponse.ok) {
                    console.error('Error reporting issue:', errorResponse.statusText);
                    return;
                }
            } catch (error) {
                console.error('Error reporting issue:', error);
                return;
            }
        }

        try {
            const response = await fetch(`${API_BASE}/completed-po/`, {
                method: 'POST',
                headers: {
                    'Authorization': `Token ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ po_number: ponumber }),
            });

            if (response.ok) {
                console.log('Pedido completado con éxito');

                // The load is done: drop what Beta #1 kept on this device.
                clearSavedLoad(ponumber);

                // Update cache to remove the completed order
                const cached = localStorage.getItem(`orders_paid`);
                if (cached) {
                    try {
                        const { timestamp, data } = JSON.parse(cached);
                        // Remove the order from the list
                        const updatedData = data.filter(order => order.po_number !== ponumber);
                        localStorage.setItem(`orders_paid`, JSON.stringify({
                            timestamp,
                            data: updatedData
                        }));
                        console.log('Removed PO from cache:', ponumber);
                    } catch (e) {
                        console.error('Error updating cached data:', e);
                    }
                }

                // Call the callback if it exists
                if (location.state?.onOrderChecked) {
                    location.state.onOrderChecked(ponumber);
                }
                navigate('/carga');
            } else {
                console.error('Error al completar el pedido', response.status, response.statusText);
            }
        } catch (error) {
            console.error('Error:', error);
        }
    };

    const handleCancelOrder = () => {
        const confirmExit = window.confirm("¿Está seguro que desea cancelar la operación?");
        if (confirmExit) {
            navigate(`/paid/${ponumber}`);
        }
    };

    const handleBetaMode = () => {
        setShowBetaMode(true);
    };

    const handleBackToNormal = () => {
        setShowBetaMode(false);
        setScannedItems([]);
        setCurrentBarcode('');
        setCurrentQuantity('');
        setReceipt(null);
    };

    const handleBarcodeSubmit = (e) => {
        e.preventDefault();
        if (!currentBarcode || !currentQuantity) {
            alert('Por favor ingrese el código de barras y la cantidad');
            return;
        }

        // Exact barcode first; a variable-weight "25…" code falls back to its
        // 7-digit product identifier (see utils/loadingScan.js).
        const poItem = findLineForCode(poData?.items, currentBarcode);
        if (!poItem) {
            alert('Producto no encontrado en este pedido');
            return;
        }

        const quantity = parseFloat(currentQuantity);
        if (!(quantity > 0)) {
            alert('La cantidad debe ser mayor que cero');
            return;
        }

        // Kilo lines accumulate piece by piece; unit lines replace, as before.
        setScannedItems((previous) => recordScan(previous, poItem, quantity));
        setReceipt({ item: poItem, quantity, before: scannedItems });

        // Clear inputs
        setCurrentBarcode('');
        setCurrentQuantity('');
    };

    const handleBarcodeChange = (value) => {
        // Trim any trailing spaces from barcode scanner
        const trimmedValue = value.trim();
        setCurrentBarcode(trimmedValue);

        // Kilo lines get the weight printed in the code, frozen unit lines the
        // ordered quantity. The loader can still overwrite the field.
        const poItem = findLineForCode(poData?.items, trimmedValue);
        const suggested = poItem ? suggestedQuantity(poItem, trimmedValue) : null;
        if (suggested !== null) {
            setCurrentQuantity(suggested.toString());
        }
    };

    const getScannedQuantity = (skuCode) => {
        const scanned = scannedItems.find(item => item.sku_code === skuCode);
        return scanned ? scanned.scanned_quantity : 0;
    };

    const isItemCompletelyScanned = (item) => isLineComplete(item, getScannedQuantity(item.sku_code));

    const allItemsScanned = () => {
        if (!poData?.items) return false;
        return poData.items.every(item => isItemCompletelyScanned(item));
    };

    const removeScannedItem = (skuCode) => {
        setScannedItems(scannedItems.filter(item => item.sku_code !== skuCode));
        setReceipt(null);
    };

    const handleUndo = () => {
        setScannedItems(receipt.before);
        setReceipt(null);
        document.getElementById('barcode')?.focus();
    };

    if (showBetaMode) {
        return (
            <div className="completar-pedido-container">
                <div className="beta-scanner-container">
                    <div className="beta-header">
                        <button onClick={handleBackToNormal} className="back-button">
                            ← Volver
                        </button>
                        <h1>Verificación por Código de Barras - PO {ponumber}</h1>
                        <span className="beta-badge">BETA</span>
                    </div>

                    <form onSubmit={handleBarcodeSubmit} className="barcode-form">
                        <div className="barcode-inputs">
                            <div className="input-group">
                                <label htmlFor="barcode">Código de Barras:</label>
                                <input
                                    id="barcode"
                                    type="text"
                                    value={currentBarcode}
                                    onChange={(e) => handleBarcodeChange(e.target.value)}
                                    placeholder="Escanee o ingrese código"
                                    autoFocus
                                />
                            </div>
                            <div className="input-group">
                                <label htmlFor="quantity">Cantidad:</label>
                                <input
                                    id="quantity"
                                    type="number"
                                    step="any"
                                    value={currentQuantity}
                                    onChange={(e) => setCurrentQuantity(e.target.value)}
                                    placeholder="Cantidad"
                                    min="0"
                                />
                            </div>
                            <button type="submit" className="scan-button">Agregar</button>
                        </div>
                    </form>

                    <ScanFeedback
                        identified={findLineForCode(poData?.items, currentBarcode)}
                        receipt={receipt}
                        scannedQuantity={getScannedQuantity}
                        onUndo={handleUndo}
                    />

                    <div className="items-progress">
                        <h3>Progreso del Pedido ({scannedItems.length}/{poData?.items?.length || 0} productos verificados)</h3>

                        <div className="items-grid">
                            {poData?.items?.map((item, index) => {
                                const scannedQty = getScannedQuantity(item.sku_code);
                                const isComplete = isItemCompletelyScanned(item);

                                return (
                                    <div key={index} className={`item-card ${isComplete ? 'complete' : scannedQty > 0 ? 'partial' : 'pending'}`}>
                                        <div className="item-info">
                                            <h4>{item.sku_name}</h4>
                                            <p>SKU: {item.sku_code}</p>
                                            <p>Pedido: {item.ordered_quantity} {item.format_name}</p>
                                            {item.format_unit_kg && (
                                              <p>Pickeado: {item.actual_quantity} {item.format_name}</p>
                                            )}
                                            <p>Verificado: {scannedQty} {item.format_name}</p>
                                        </div>
                                        <div className="item-status">
                                            {isComplete ? (
                                                <span className="status-complete">✓</span>
                                            ) : scannedQty > 0 ? (
                                                <span className="status-partial">◐</span>
                                            ) : (
                                                <span className="status-pending">○</span>
                                            )}
                                        </div>
                                        {scannedQty > 0 && (
                                            <button
                                                onClick={() => removeScannedItem(item.sku_code)}
                                                className="remove-item"
                                            >
                                                ×
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="beta-actions">
                        {allItemsScanned() ? (
                            <button onClick={handleCompleteOrder} className="complete-order-button">
                                Finalizar Pedido
                            </button>
                        ) : (
                            <p className="pending-message">
                                Escanee todos los productos para finalizar el pedido
                            </p>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="completar-pedido-container">
            <div className="pedido-completado-box">
                <h1>¿Pedido {ponumber} completado?</h1>

                <div className="beta-button-container">
                    <button onClick={handleBetaMode} className="beta-button">
                        Beta
                    </button>
                </div>

                <label>
                    <input type="checkbox" checked={reportError} onChange={() => setReportError(!reportError)} />
                    Reportar error
                </label>
                {reportError && (
                    <div className="error-report-section">
                        <label>
                            Tipo de error:
                            <select value={errorType} onChange={(e) => setErrorType(e.target.value)}>
                                <option value="">Seleccione una opción</option>
                                <option value="quantity">Cantidad</option>
                                <option value="tags">Etiquetas</option>
                                <option value="sku">Producto</option>
                            </select>
                        </label>
                        <label>
                            Explicación del error:
                            <textarea value={errorExplanation} onChange={(e) => setErrorExplanation(e.target.value)} />
                        </label>
                    </div>
                )}
                <div className="button-container">
                    <button className="yes-button" onClick={handleCompleteOrder}>Sí</button>
                    <button className="no-button" onClick={handleCancelOrder}>No</button>
                </div>
            </div>
        </div>
    );
}

export default Completarpedido;
